import { Api } from 'src/common';
import { OnboardingDocument } from 'src/features/common';
import { OnboardingDocumentTypeEnum } from 'src/features/onboarding/types/upload-documents';

const isSmartCard = (docArray: DocumentWithOptionalBody[]) =>
  docArray.every((doc) => doc.documentType === OnboardingDocumentTypeEnum.ID_CARD);

interface ImageResponse {
  ok: boolean;
  status: number;
  image: string;
}

type DocumentWithOptionalBody = OnboardingDocument & {
  documentBody?: string;
  initial?: boolean;
};

const joinImages = async (
  id: string,
  image1Base64: string,
  image2Base64: string,
  abortController: AbortController,
): Promise<string> => {
  const endpoint = '/imagejoin';

  const result = await Api.post<ImageResponse>(endpoint, {
    signal: abortController.signal,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      id: id,
      first: image1Base64,
      second: image2Base64,
    }),
  });

  if (result && result.status === 200 && result.response.ok) {
    return result.response.image;
  }

  throw new Error(
    `Image Join Revamp: Invalid response: ${result.status}: ${JSON.stringify(result.response)}`,
  );
};

const joinDocs = async (
  id: string,
  documents: DocumentWithOptionalBody[],
): Promise<DocumentWithOptionalBody> => {
  const firstDoc = documents[0];
  const secondDoc = documents[1];

  if (!firstDoc.documentBody || !secondDoc.documentBody) {
    throw new Error('Upload docs Revamp: No body in images');
  }

  const merged = await ImageService.joinImages(
    id,
    firstDoc.documentBody,
    secondDoc.documentBody,
    new AbortController(),
  );

  return {
    documentName: `${id}.png`,
    documentType: firstDoc.documentType,
    documentBody: merged,
    idNumber: id,
  };
};

async function joinDocsById(
  documents: DocumentWithOptionalBody[],
): Promise<DocumentWithOptionalBody[]> {
  const noIdKey = 'no-id';
  const docMap = new Map<string, DocumentWithOptionalBody[]>();

  documents.forEach((doc) => {
    if (!doc.idNumber) {
      docMap.set(noIdKey, [...(docMap.get(noIdKey) || []), doc]);
      return;
    }

    if (docMap.get(doc.idNumber)) {
      docMap.set(doc.idNumber, [...(docMap.get(doc.idNumber) || []), doc]);
    } else {
      docMap.set(doc.idNumber, [doc]);
    }
  });

  const result: Promise<DocumentWithOptionalBody>[] = [];

  docMap.forEach((docArray, key) => {
    if (docArray.length === 2 && key !== noIdKey && isSmartCard(docArray)) {
      result.push(joinDocs(key, docArray));
      return;
    }

    result.push(...docArray.map((doc) => Promise.resolve(doc)));
  }, []);

  return await Promise.all(result);
}

export const ImageService = { joinImages, joinDocsById };
